/**
 * Phase A: registers Network realtime router with BlissLiveSync + hard-sync event listener.
 * Handler bodies moved from NetworkDataContext useEffects (subscriptions now in BlissLiveSyncProvider).
 */
import { useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseGuard';
import { getPublicUserV1ById } from '@/services/publicUsersV1Service';
import type { BlissNetNetworkRealtimeEvent } from '@/contexts/blissLiveSync/blissLiveSyncTypes.events';
import type { BlissNetNetworkRouter } from '@/contexts/blissLiveSync/blissLiveSyncTypes';
import { BLISSNET_HARD_SYNC_REQUEST } from '@/contexts/blissLiveSync/blissnetLiveEvents';
import {
  isApprovalToastEligible,
  shouldHardSyncOnConnectionApproval
} from '@/contexts/network/connectionApprovalSync';

type Deps = {
  registerNetworkRouter: (router: BlissNetNetworkRouter) => () => void;
  user: { id: string } | null;
  isReady: boolean;
  scheduleHardSync: (reason: string) => void;
  refreshConnectionRequests: () => Promise<void>;
  setConnectionRequests: Dispatch<SetStateAction<any[]>>;
  loadAllNetworkData: (opts?: { force?: boolean }) => Promise<void>;
  forceHardSync: (reason: string) => Promise<void>;
  refreshNetwork: (opts?: { reason?: 'explicit'; force?: boolean }) => Promise<unknown>;
  setNetworkEpoch: Dispatch<SetStateAction<number>>;
  navigateToNetwork: (opts: { tab?: string }) => void;
  clearPersistedTabCacheOnly: () => void;
  clearProfileStatsCache: () => void;
};

export function useNetworkBlissLiveSyncEffects(deps: Deps) {
  const {
    registerNetworkRouter,
    user,
    isReady,
    scheduleHardSync,
    refreshConnectionRequests,
    setConnectionRequests,
    loadAllNetworkData,
    forceHardSync,
    refreshNetwork,
    setNetworkEpoch,
    navigateToNetwork,
    clearPersistedTabCacheOnly,
    clearProfileStatsCache
  } = deps;

  const processedConnectionsRef = useRef<Set<string>>(new Set());

  const networkRealtimeRouter = useCallback(
    async (event: BlissNetNetworkRealtimeEvent) => {
      if (!user?.id || !isReady) return;

      switch (event.type) {
        case 'profiles_discovery_visibility':
          await loadAllNetworkData({ force: true });
          return;

        case 'connection_request': {
          const payload = event.payload;
          if (payload.eventType === 'DELETE') {
            const row = payload.old || {};
            const deletedId = row.id;
            if (deletedId) {
              setConnectionRequests((prev) => (prev || []).filter((r: any) => r.id !== deletedId));
            }
            return;
          }

          const newStatus = payload.new?.status?.toLowerCase();
          if (newStatus !== 'pending') return;

          scheduleHardSync(`rt:requests:${payload.eventType}`);
          refreshConnectionRequests();

          const newRequest = payload.new;
          try {
            const senderData = await getPublicUserV1ById(newRequest.sender_id);
            const senderName = senderData?.name || 'Someone';
            const senderPhoto = senderData?.photo_urls?.[0];

            if (typeof window !== 'undefined') {
              import('../../utils/userGestureDetector').then(({ userGestureDetector }) => {
                userGestureDetector.safeVibrate([100, 50, 100]);
              });
            }

            toast.success(`${senderName} wants to connect with you!`, {
              description: 'Tap to view their friend request',
              duration: 6000,
              icon: '👋',
              action: {
                label: 'View',
                onClick: () => {
                  navigateToNetwork({ tab: 'alerts' });
                }
              }
            });

            const { notificationService } = await import('@/services/notifications/notificationService');
            await notificationService.sendConnectionRequestNotification({
              id: newRequest.id,
              requesterId: newRequest.sender_id,
              requesterName: senderName,
              requesterPhoto: senderPhoto,
              inviteCode: newRequest.invite_code || '',
              timestamp: new Date(newRequest.created_at)
            });
          } catch (error) {
            console.error('❌ V167.NOTIFY: Exception showing notification:', error);
            toast.success('🎉 New Friend Request!', {
              description: 'Someone wants to connect with you',
              duration: 5000,
              action: {
                label: 'View',
                onClick: () => {
                  navigateToNetwork({ tab: 'alerts' });
                }
              }
            });
          }
          return;
        }

        case 'connection_approval': {
          const payload = event.payload;
          const newConnection = payload.new as any;
          const oldConnection = payload.old as any;

          if (
            !shouldHardSyncOnConnectionApproval({
              currentUserId: user.id,
              newConnection,
              oldConnection,
              processedIds: processedConnectionsRef.current
            })
          ) {
            return;
          }

          processedConnectionsRef.current.add(newConnection.id);

          // Unconditional participant sync — must not depend on request lookup / toast.
          try {
            localStorage.removeItem('network_tab_cache');
          } catch (e) { }
          clearProfileStatsCache?.();
          setNetworkEpoch((x) => x + 1);
          scheduleHardSync('rt:connection_approval');

          const otherUserId =
            newConnection.user_id === user.id
              ? newConnection.connection_user_id
              : newConnection.user_id;

          try {
            const { data: requestData, error: requestError } = await supabase
              .from('connection_requests')
              .select('sender_id, recipient_id, status')
              .or(
                `and(sender_id.eq.${user.id},recipient_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},recipient_id.eq.${user.id})`
              )
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            if (requestError) {
              console.error('❌ V172.FIX2: Error fetching connection request:', requestError);
              return;
            }

            if (
              !isApprovalToastEligible({
                currentUserId: user.id,
                requestSenderId: requestData?.sender_id,
                requestLookupFailed: false
              })
            ) {
              return;
            }

            const accepterData = await getPublicUserV1ById(otherUserId);
            const accepterName = accepterData?.name || 'Someone';

            if (typeof window !== 'undefined' && 'vibrate' in navigator) {
              navigator.vibrate([200, 100, 200]);
            }

            toast.success(`${accepterName} accepted your friend request! 🎉`, {
              description: 'Tap to view your connection requests',
              duration: 6000,
              icon: '✅',
              action: {
                label: 'View',
                onClick: () => {
                  setTimeout(() => {
                    navigateToNetwork({ tab: 'alerts' });
                  }, 100);
                }
              }
            });
          } catch (error) {
            // Sync already scheduled; toast is best-effort only.
            console.error('❌ V172.FIX2: Exception showing approval notification:', error);
          }
          return;
        }

        case 'connection_disconnect': {
          const payload = event.payload;
          const variant = event.variant;

          if (variant === 'other_delete') {
            const oldConnection = payload.old as any;
            const disconnectedUserId = oldConnection?.user_id;
            const userData = await getPublicUserV1ById(disconnectedUserId);
            const userName = userData?.name || 'Someone';
            const { toast: toastFn } = await import('sonner');
            toastFn.error(`${userName} disconnected from you`, {
              description: 'Your connection has been removed.'
            });
            setTimeout(() => {
              try {
                localStorage.removeItem('network_tab_cache');
              } catch (e) { }
              setNetworkEpoch((x) => x + 1);
              void refreshNetwork({ force: true });
              navigateToNetwork({});
            }, 1500);
            return;
          }

          if (variant === 'other_update') {
            const oldConnection = payload.old as any;
            const newConnection = payload.new as any;
            const isDisconnection =
              newConnection.status === 'DISCONNECTED' && oldConnection.status !== 'DISCONNECTED';
            const isBlocked =
              newConnection.status === 'BLOCKED' && oldConnection.status !== 'BLOCKED';
            if (!isDisconnection && !isBlocked) return;

            const otherUserId = newConnection.user_id;
            const userData = await getPublicUserV1ById(otherUserId);
            const userName = userData?.name || 'Someone';
            const { toast: toastFn } = await import('sonner');
            if (isBlocked) {
              toastFn.error(`${userName} has blocked you`, {
                description: 'You will no longer see each other on BlissNet.'
              });
            } else {
              toastFn.error(`${userName} disconnected from you`, {
                description: 'Your connection has been removed.'
              });
            }
            setTimeout(() => {
              try {
                localStorage.removeItem('network_tab_cache');
              } catch (e) { }
              setNetworkEpoch((x) => x + 1);
              void refreshNetwork({ force: true });
              navigateToNetwork({});
            }, 1500);
            return;
          }

          if (variant === 'self_delete') {
            setTimeout(() => {
              try {
                localStorage.removeItem('network_tab_cache');
              } catch (e) { }
              setNetworkEpoch((x) => x + 1);
              void refreshNetwork({ force: true });
              navigateToNetwork({});
            }, 1200);
            return;
          }

          if (variant === 'self_update') {
            const oldConnection = payload.old as any;
            const newConnection = payload.new as any;
            const isDisconnection =
              newConnection.status === 'DISCONNECTED' && oldConnection.status !== 'DISCONNECTED';
            const isBlocked =
              newConnection.status === 'BLOCKED' && oldConnection.status !== 'BLOCKED';
            if (!isDisconnection && !isBlocked) return;

            setTimeout(() => {
              try {
                localStorage.removeItem('network_tab_cache');
              } catch (e) { }
              setNetworkEpoch((x) => x + 1);
              void refreshNetwork({ force: true });
              navigateToNetwork({});
            }, 1200);
          }
          return;
        }

        case 'user_event_insert': {
          const payload = event.payload;
          const row = payload.new as any;
          if (!row?.id) return;
          if (row.event_type !== 'connection.accepted') return;

          const eventKey = `v320_event_once_${row.id}`;
          if (typeof window !== 'undefined') {
            if (sessionStorage.getItem(eventKey)) {
              return;
            }
            sessionStorage.setItem(eventKey, String(Date.now()));
          }

          setNetworkEpoch((x) => x + 1);
          clearPersistedTabCacheOnly();
          clearProfileStatsCache();

          await forceHardSync('user_events:connection.accepted');

          toast.success('Friend request accepted! Updating…', { duration: 2000 });

          try {
            await supabase.from('user_events').delete().eq('id', row.id);
          } catch (err) {
            console.warn('[V320.USER_EVENTS] delete failed (non-blocking):', err);
          }
          return;
        }

        default:
          return;
      }
    },
    [
      user?.id,
      isReady,
      scheduleHardSync,
      refreshConnectionRequests,
      setConnectionRequests,
      loadAllNetworkData,
      forceHardSync,
      refreshNetwork,
      setNetworkEpoch,
      navigateToNetwork,
      clearPersistedTabCacheOnly,
      clearProfileStatsCache
    ]
  );

  useEffect(() => {
    const off = registerNetworkRouter(networkRealtimeRouter);
    void refreshConnectionRequests();
    return off;
  }, [registerNetworkRouter, networkRealtimeRouter, refreshConnectionRequests]);

  useEffect(() => {
    const onHardSyncRequest = (e: Event) => {
      const ce = e as CustomEvent<{ reason?: string }>;
      const reason = ce.detail?.reason;
      if (reason) void forceHardSync(reason);
    };
    window.addEventListener(BLISSNET_HARD_SYNC_REQUEST, onHardSyncRequest as EventListener);
    return () =>
      window.removeEventListener(BLISSNET_HARD_SYNC_REQUEST, onHardSyncRequest as EventListener);
  }, [forceHardSync]);
}
